import React from 'react';
import { createPortal } from 'react-dom';
import ConfirmationDialog, { ConfirmationType } from '../confirmation_dialog/ConfirmationDialog';

export interface LockConfirmationDialogProps {
  open: boolean;
  targetLocked: boolean; // true = locking, false = unlocking
  itemType: 'Test Case' | 'Test Plan' | 'Validation Config' | 'Setup' | 'Session' | 'Item' | 'Satellite Group Validation';
  count?: number; // for bulk operations (e.g. 3)
  itemName?: string; // for single item operations
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  dataTestIdPrefix?: string;
}

/**
 * Standard confirmation dialog for validation Lock & Unlock actions.
 * Wraps ConfirmationDialog with tailored copy for single and bulk operations.
 * Portalled to document.body to ensure it renders atop all cards, rows, and backdrop filters.
 */
export const LockConfirmationDialog: React.FC<LockConfirmationDialogProps> = ({
  open,
  targetLocked,
  itemType,
  count,
  itemName,
  loading = false,
  onConfirm,
  onCancel,
  dataTestIdPrefix = 'lock-confirm',
}) => {
  if (!open) return null;

  const isBulk = count !== undefined && count > 0;
  const dialogType: ConfirmationType = targetLocked ? 'warning' : 'update';

  let title = '';
  let description = '';
  const actionWord = targetLocked ? 'Lock' : 'Unlock';
  const loadingWord = targetLocked ? 'Locking' : 'Unlocking';

  if (isBulk) {
    const pluralType = count === 1 ? itemType : `${itemType}s`;
    title = `${actionWord} ${pluralType}?`;
    if (targetLocked) {
      description = `Locking selected ${pluralType.toLowerCase()} will make them read-only and prevent modifications or deletion until they are unlocked.`;
      if (itemType === 'Test Plan') {
        description += ' All test cases included in these plans will also be locked.';
      }
    } else {
      description = `Unlocking selected ${pluralType.toLowerCase()} will restore edit permissions and allow modifications or deletion.`;
      if (itemType === 'Test Plan') {
        description += ' Test cases shared by other locked plans will remain locked.';
      }
    }
  } else {
    const displayName = itemName ? `"${itemName}"` : `this ${itemType.toLowerCase()}`;
    title = `${actionWord} ${itemType}?`;
    if (targetLocked) {
      description = `Locking ${displayName} will make it read-only and prevent modifications or deletion until it is unlocked.`;
      if (itemType === 'Test Plan') {
        description += ' All test cases included in this plan will also be locked.';
      } else if (itemType === 'Satellite Group Validation') {
        description = `Locking validation for ${displayName} will lock all Test Plans, Test Cases, Default Telemetry Configs, Setups, and Sessions.`;
      }
    } else {
      description = `Unlocking ${displayName} will restore edit permissions and allow modifications or deletion.`;
      if (itemType === 'Test Plan') {
        description += ' Test cases shared by other locked plans will remain locked.';
      } else if (itemType === 'Satellite Group Validation') {
        description = `Unlocking validation for ${displayName} will unlock all validation resources for this satellite group.`;
      }
    }
  }

  const dialogElement = (
    <ConfirmationDialog
      type={dialogType}
      open={open}
      title={title}
      description={description}
      loading={loading}
      confirm_button_text={actionWord}
      cancel_button_text="Cancel"
      confirm_loading_text={loadingWord}
      onConfirm={onConfirm}
      onCancel={onCancel}
      dataTestIdPrefix={dataTestIdPrefix}
    />
  );

  if (typeof document !== 'undefined') {
    return createPortal(dialogElement, document.body);
  }

  return dialogElement;
};

export default LockConfirmationDialog;

